import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { api, extractError } from "../lib/api";
import { Logo } from "../components/Logo";

export default function FeedbackPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/public/feedback/${token}`)
      .then((r) => alive && setCtx(r.data))
      .catch((e) => alive && setError(extractError(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await api.post(`/public/feedback/${token}`, { rating, comment: comment.trim() || null });
      setDone(true);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)] flex flex-col">
      <header className="px-6 sm:px-10 py-5 border-b border-[var(--gt-line)] bg-white">
        <Logo size="md" />
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-lg gt-card bg-white" data-testid="feedback-card">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--gt-ink-2)]" data-testid="feedback-loading">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : error && !ctx ? (
            <div className="text-sm text-red-600" data-testid="feedback-error">{error}</div>
          ) : done || ctx?.already_submitted ? (
            <div className="text-center py-6" data-testid="feedback-thanks">
              <CheckCircle2 size={40} className="mx-auto text-emerald-600 mb-3" />
              <h1 className="font-heading text-2xl font-black tracking-tight">Thanks for your feedback!</h1>
              <p className="mt-2 text-sm text-[var(--gt-ink-2)]">
                {ctx?.already_submitted && !done
                  ? "You've already rated this event — thanks again."
                  : "Your rating helps organisers put on better events."}
              </p>
              <Link to="/events" className="inline-block mt-6 text-sm underline underline-offset-2" data-testid="feedback-browse-link">
                Browse upcoming events
              </Link>
            </div>
          ) : (
            <>
              <div className="gt-label mb-2">Rate your experience</div>
              <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                How was {ctx?.event_title || "the event"}?
              </h1>
              {ctx?.organiser_name && (
                <p className="mt-1 text-xs text-[var(--gt-ink-3)]">Presented by {ctx.organiser_name}</p>
              )}
              <div className="flex gap-2 mt-6" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    data-testid={`feedback-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      size={34}
                      className={
                        n <= (hover || rating)
                          ? "text-[var(--gt-accent)] fill-[var(--gt-accent)]"
                          : "text-[var(--gt-ink-3)]"
                      }
                    />
                  </button>
                ))}
              </div>
              <label className="gt-form-label mt-6 block">Anything to add? <span className="font-normal text-[var(--gt-ink-3)]">(optional)</span></label>
              <textarea
                className="gt-input min-h-[100px]"
                maxLength={2000}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What did you love? What could be better?"
                data-testid="feedback-comment-input"
              />
              {error && <div className="mt-2 text-xs text-red-600" data-testid="feedback-submit-error">{error}</div>}
              <button
                type="button"
                onClick={submit}
                disabled={!rating || submitting}
                className="gt-btn-primary mt-5 w-full disabled:opacity-40"
                data-testid="feedback-submit-btn"
              >
                {submitting ? "Sending…" : "Send feedback"}
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
